import {AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {InfoOverlayComponent} from "../../dialog/noclosing/info-overlay.component";
import {CameraService} from "../services/camera.service";
import {MatDialog} from "@angular/material/dialog";
import {DomSanitizer} from "@angular/platform-browser";
import {HttpClient, HttpEventType} from "@angular/common/http";
import {WebsocketService} from "../services/websocket.service";
import {TranslateService} from "@ngx-translate/core";
import {MatSnackBar} from "@angular/material/snack-bar";
import {ActivatedRoute, Router} from "@angular/router";
import {HeizcheckBackendService} from "../services/backend.service";
import {HeizcheckRequestService} from "../services/heizcheck-request.service";
import {HeizcheckConstants} from "../heizcheck.constants";
import {ElementService} from "../../services/element.service";
import {EmbedService} from "../../services/embed.service";

@Component({
    selector: 'app-foto',
    templateUrl: './foto.component.html',
    styleUrls: [],
    standalone: false
})
export class FotoComponent implements OnInit, OnDestroy, AfterViewInit {

  /**
   * toggle if the camera is available on the device or not.
   */
  cameraAvailable = false;

  /**
   * error key for the case that the camera is not available.
   */
  cameraError: string;

  /**
   * toggle for talking a photo
   */
  takePhoto = false;

  /**
   * data of photos that where taken.
   */
  photos = [];

  /**
   * current photoid for taking pictures
   */
  currentPhoto: number = null;

  /**
   * index of the photo that should be shown as preview
   */
  imageCheck: any = null;

  /**
   * indicator if the initial call was from the desktop or mobile.
   */
  fromDesktop: boolean = false;

  /**
   * select measurment service provider (Messdienstleister)
   */
  msp: string = "";

  /**
   * the uploaded percentage of the data
   */
  uploadPercentage: number = null;

  /**
   * Holds the terms check
   */
  terms: boolean = false;

  /**
   * placeholder for an error message
   */
  error: string;

  /**
   * element for scrolling to the top
   */
  @ViewChild('top') top: ElementRef;

  /**
   * The imagecheck wrapper
   * @private
   */
  @ViewChild('imagecheck') imagecheck: ElementRef;

  /**
   *
   * @param cameraService
   * @param dialog
   * @param sanitizer
   * @param http
   * @param backendService
   * @param websockeService
   * @param translate
   * @param snackBar
   * @param router
   * @param route
   * @param requestService
   * @param ngZone
   * @param elementService
   * @param embedService
   */
  constructor(private cameraService: CameraService, public dialog: MatDialog, private sanitizer: DomSanitizer,
    protected http: HttpClient, private backendService: HeizcheckBackendService, private websockeService: WebsocketService,
    private translate: TranslateService, public snackBar: MatSnackBar, private router: Router, private route: ActivatedRoute,
    private requestService: HeizcheckRequestService, private ngZone: NgZone, private elementService: ElementService, private embedService: EmbedService) { }

  /**
   * @inheritDoc
   */
  ngAfterViewInit(): void {
    this.elementService.scrollToElement(this.top.nativeElement);
  }

  /**
   * Init function for the component.
   */
  ngOnInit(): void {
    this.fromDesktop = this.backendService.isRequestIdInUrl();

    this.testCameraCapabilities();
  }

  testCameraCapabilities(): void {
    // show a dialog that we do check the system now
    const dialogRef = this.dialog.open(InfoOverlayComponent,{
      disableClose: true,
      data: {
        title: 'heizcheck.foto.overlay.systemcheck.title',
        body: 'heizcheck.foto.overlay.systemcheck.body',
        animation: true
      }
    });

    // start system check
    this.cameraService.testCameraCapabilities().subscribe(result => {
      // test is done, close the loading dialog
      dialogRef.close();
      console.debug("cameraAvailable", result);

      if (result === CameraService.STATUS_OK) {
        // everything is okay, user can make photos.
        this.cameraAvailable = true;
        // setTimeout(() => this.updateBackendStatus(), 1000);

        // register on websocket server
        this.backendService.getRequestId().subscribe((id) => {
          this.initWebsocket(id);
        });

        // send empyt images to backend to make sure we are starting blank
        this.sendPreviewimagesToBackend();

        // for dev purpose only
        // this.startCamera(null);
      }
      else {
        this.cameraAvailable = false;
        this.cameraError = 'heizcheck.foto.error.' + result;
      }
    });
  }

  /**
   * Starts the camera.
   * @param event
   * @param photoId
   */
  startCamera(event, photoId = null) {
    if(event) {
      event.preventDefault();
    }
    if(photoId !== null) {
      this.currentPhoto = photoId;
    }
    this.imageCheck = null;
    this.takePhoto = true;

    this.backendService.getRequestId().subscribe((id) => {
      this.websockeService.sendMessage(id, HeizcheckConstants.WEBSOCKET_SENDER_MOBILE, HeizcheckConstants.WEBSOCKET_MESSAGE_STARTCAMERA);
    });
  }

  /**
   * Eventhanlder if the camera was stopped. Get's an image if there is one or nothing.
   * @param result
   */
  cameraStopped(result) {
    if(result) {
      if(result instanceof HTMLCanvasElement) {
        // we got a canvas
        result.toBlob((blob) => {
          this.savePhoto(blob)
        }, 'image/jpeg', 1);
      }
      else {
        this.savePhoto(result);
      }
    }
    else {
      // taking photo was stopped by the user
      this.currentPhoto = null;
    }
    this.backendService.getRequestId().subscribe((id) => {
      this.websockeService.sendMessage(id, HeizcheckConstants.WEBSOCKET_SENDER_MOBILE, HeizcheckConstants.WEBSOCKET_MESSAGE_STOPCAMERA);
    });

    this.takePhoto = false;
  }

  /**
   * save the photo and send a preview to the backend
   * @param blob
   */
  savePhoto(blob): void {
    // save the image to the photo list.
    const url = URL.createObjectURL(blob);
    if(this.currentPhoto === null) {
      this.currentPhoto = this.photos.length;
    }

    this.ngZone.run( () => {
      this.photos[this.currentPhoto] = {
        'blob': blob,
        'previewBlob': blob,
        'url': this.sanitizer.bypassSecurityTrustUrl(url)
      };
    });

    this.resizePreview(this.currentPhoto);
    this.currentPhoto = null;
  }

  /**
   * Resize an image from the photos array to a smaller, preview version.
   * @param index
   * @private
   */
  private resizePreview(index: number): void {
    const that = this;

    // create image and add onload funktion to start loading the image
    const img = new Image();
    img.onload = function (event) {
      const image = <HTMLImageElement>this;

      // create a smaller canvas an copy the image there, afterwards copy the blod and send to the server
      const smallCanvas = document.createElement('canvas');
      smallCanvas.width = 200;
      smallCanvas.height = Math.round(smallCanvas.width / (image.width / image.height));
      const context = smallCanvas.getContext("2d");
      context.drawImage(image,0,0,image.width, image.height,0,0, smallCanvas.width, smallCanvas.height);
      smallCanvas.toBlob(blob => {
        // read blob from smaller canvas
        that.photos[index].previewBlob = blob;

        // send preview images to backend
        that.sendPreviewimagesToBackend();
      }, 'image/jpeg', 0.7);
    };
    img.src = URL.createObjectURL(this.photos[index].blob);
  }

  /**
   * Show the imagecheck overlay
   * @param event
   * @param photoId
   */
  showImageCheck(event, photoId): void {
    event.preventDefault();
    event.stopPropagation();
    // show the overlay and set the image
    this.imageCheck = photoId;

    if(this.embedService.isEmbedded() === true) {
      setTimeout(() => this.elementService.scrollToElement(this.imagecheck.nativeElement), 100);
    }
  }

  /**
   * Close the imagecheck overlay
   */
  closeImageCheck(): void {
    this.imageCheck = null;
  }

  /**
   * Deletes a phot from the current set and closes the check overlay.
   * @param photoId
   */
  deletePhoto(photoId: number): void {
    console.debug("deletePhoto", photoId)
    this.photos.splice(photoId, 1);
    this.imageCheck = null;

    // inform backend about updates
    this.sendPreviewimagesToBackend();
  }

  /**
   * init the websocket connection and handle message from server.
   * @param id
   */
  initWebsocket(id: string): void {
    this.websockeService.connect(id, HeizcheckConstants.WEBSOCKET_SENDER_MOBILE);
    this.websockeService.messages$.subscribe(message => {
      console.debug("mobile - got message on mobile ", message);
      switch(message['message']) {
        case 'connection_lost':
          if(this.fromDesktop === true) {
            this.translate.get(['heizcheck.foto.snackbar.error.connectionlost.mobile', 'output.erfassen.snackbar.ok']).subscribe(texts => {
              this.snackBar.open(texts['heizcheck.foto.snackbar.error.connectionlost.mobile'], texts['output.erfassen.snackbar.ok'], {
                panelClass: 'error',
                verticalPosition: HeizcheckConstants.SNACKBAR_POSITION_VERTICAL
              });
            });
          }
          break;
        case 'connection_back':
          // close the snackbar if there is one open
          this.snackBar.dismiss();
          break;
      }
    });
  }

  /**
   * Informs the backend about status updates while taking photos.
   * Sends preview images if there are some.
   */
  sendPreviewimagesToBackend(): void {
    this.backendService.getRequestId().subscribe((id) => {
      const data = new FormData();
      if(this.photos.length > 0) {
        for(let i=0; i<this.photos.length; i++) {
          const photoName = i < 10? '0' + i : i;
          data.append('photo[]', this.photos[i].previewBlob, 'photo' + photoName + '.jpg');
        }
      }
      this.http.post('/smh/foto/update/' + id, data).subscribe(
        result => {
          console.debug("sending update to the backend was successful");
          this.backendService.getRequestId().subscribe((id) => {
            this.websockeService.sendMessage(id, HeizcheckConstants.WEBSOCKET_SENDER_MOBILE, HeizcheckConstants.WEBSOCKET_MESSAGE_IMAGEUDPATE);
          });
        },
        error => {
          console.error("Error while sending update to the backend.", error)
        }
      );
    });
  }

  /**
   * Uploads the files to the server.
   */
  uploadData(): void {
    if(this.photos.length === 0 || !this.terms || !this.msp) {
      this.error = ".forminvalid"
      // do nothing, there is nothing to uploads
      return;
    }

    this.error = null;

    this.backendService.getRequestId().subscribe((id) => {
      // inform desktop about upload
      this.websockeService.sendMessage(id, HeizcheckConstants.WEBSOCKET_SENDER_MOBILE, HeizcheckConstants.WEBSOCKET_MESSAGE_UPLOADSTARTED);

      const data = new FormData();
      if(this.photos.length > 0) {
        for(let i=0; i<this.photos.length; i++) {
          if(this.photos[i].blob) {
            data.append('file[]', this.photos[i].blob, 'mobile-photo-' + i + '.jpg');
          }
        }
      }
      this.uploadPercentage = null;

      // before upload, send the selected msp to the backend.
      this.websockeService.sendMessage(id, HeizcheckConstants.WEBSOCKET_SENDER_MOBILE, HeizcheckConstants.WEBSOCKET_MESSAGE_MSP_SELECTED, {msp: this.msp});
      // TODO this.requestService.setMsp(this.msp);

      this.http.post('/smh/save/files/' + id, data, {reportProgress: true, observe: 'events'}).subscribe(
        response => {
          switch(response.type) {
            case HttpEventType.UploadProgress:
              // we have a upload progress information, update the counter
              this.uploadPercentage = Math.round(100 * response.loaded / response.total);
              break;
            case HttpEventType.Response:
            default:
              if(response["status"] === 200) {
                // we have a success!
                const result = response["body"]? response["body"] : null;
                if(result && result["saved"] === true) {
                  this.uploadPercentage = 100;

                  // show dialog that we start now
                  if(this.backendService.isRequestIdInUrl() === true) {
                    // inform the backend
                    this.dialog.open(InfoOverlayComponent, {
                      disableClose: true,
                      panelClass: 'center',
                      data: {
                        title: 'heizcheck.foto.overlay.upload.title',
                        body: 'heizcheck.foto.overlay.upload.body',
                        animation: false
                      }
                    });

                    // inform desktop about upload
                    this.websockeService.sendMessage(id, HeizcheckConstants.WEBSOCKET_SENDER_MOBILE, HeizcheckConstants.WEBSOCKET_MESSAGE_UPLOADFINISHED);
                  }
                  else {
                    // we stay on mobile device, go back to erfassen and start analysis
                    console.warn("TODO: start analysis")
                    this.router.navigate(['../heizkostenabrechnung/start/upload'], {relativeTo: this.route, queryParams: {startAnalysis: 1, msp: this.msp}});
                  }
                }
              }
              break;
          }
        },
        error => {
          // Show error message
          console.error("Error while sending files to the backend.", error);
          this.translate.get(['heizcheck.foto.snackbar.error.uploaderror', 'output.erfassen.snackbar.ok']).subscribe(texts => {
            this.snackBar.open(texts['heizcheck.foto.snackbar.error.uploaderror'], texts['output.erfassen.snackbar.ok'], {
              panelClass: 'error',
              verticalPosition: HeizcheckConstants.SNACKBAR_POSITION_VERTICAL
            });
          });
          this.uploadPercentage = null;
        }
      );
    });

  }

  /**
   * when the component is destroyed, close websocket connection.
   */
  ngOnDestroy(): void {
    this.websockeService.closeConnection();
  }

  /**
   * Checks if we have at least one file that should be analysed.
   */
  fileUploadPossible(): number {
    if(this.msp === "") {
      // no msp set, form can not be uploaded
      return -1;
    }

    if(this.photos.length === 0) {
      return -2;
    }

    if(this.terms === false) {
      return -3;
    }

    return 0;
  }

  /**
   * Returns the label text for the submit button
   */
  getButtonText(): string {
    switch(this.fileUploadPossible()) {
      case -1:
      case -2:
        return 'heizcheck.foto.error';
      case -3:
        return 'heizcheck.upload.terms';
      default:
        return 'heizcheck.foto.submit'
    }
  }

}
